import React, { useState, Fragment } from "react";
import { Table } from "reactstrap";
import numeral from "numeral";
import Linkify from "react-linkify";
import Lightbox from "react-image-lightbox";
import SiteCommentBox from "../SiteCommentBox";
import ApiCaller from "../../../../api/apiCaller";
import "./index.css";
import t from "utils/Lang";

interface SiteCommentInputProps {
  comment: any;
  sitePerformanceEstimationFields: Array<any>;
  isCanEdit?: boolean;
}

const SiteComment = ({
  comment,
  sitePerformanceEstimationFields,
  isCanEdit,
}: SiteCommentInputProps) => {
  const [imageBoxState, setImageBoxState] = useState({
    showLightBox: false,
    photoIndex: 0,
  });
  const [displayMode, setDisplayMode] = useState<string>("view_mode");
  const [commentData, setCommentData] = useState<any>({
    ...comment,
  });

  const onCommentSaved = async (commentAfterSaved: any) => {
    // Create mode
    const uri = `site_comment/list?id=${commentAfterSaved.id}`;
    const res = await ApiCaller.get(uri);
    setCommentData(res.result[0]);
    setDisplayMode("view_mode");
  };

  const onImageClick = (imageUrl: any, imageArray: any) => {
    imageArray.map((url: string, idx: any) => {
      if (url === imageUrl) {
        setImageBoxState({
          showLightBox: true,
          photoIndex: idx,
        });
      }
    });
  };

  let images: any = [];
  if (commentData?.attachments) {
    const imageArray = commentData?.attachments?.filter(
      (attachment: any) => attachment.file_type === "image"
    );
    images = imageArray.map((attachment: any) => attachment.file_url);
  }
  const usernameToDisplay = `${commentData.created_by?.first_name} ${commentData.created_by?.last_name}`;

  let showPerformanceEstimation = false;
  sitePerformanceEstimationFields
    ?.filter((d) => d.can_input)
    .forEach((fieldGroup: any) => {
      fieldGroup.items.forEach((field: any) => {
        if (commentData[field.code]) showPerformanceEstimation = true;
      });
    });

  return (
    <div style={styles.container}>
      <div>
        <span style={styles.username}>{usernameToDisplay}</span>
        <span style={styles.createdDate}>
          {commentData.created_on_formatted}
        </span>
        <span style={styles.editedLabel}>
          {commentData.has_edited && "(Đã chỉnh sửa)"}
        </span>
        {commentData.status && (
          <span className="site-comment__status-name">
            {t().site_listing__status}: {commentData.status.name}
          </span>
        )}
      </div>
      {displayMode === "view_mode" && (
        <div>
          <div style={styles.commentContentContainer}>
            <Linkify>
              <p style={styles.commentContentContent}>{commentData.content}</p>
            </Linkify>
          </div>
          {showPerformanceEstimation && (
            <div style={styles.performanceEstimationTable}>
              <Table responsive>
                {sitePerformanceEstimationFields
                  ?.filter((d) => d.can_input)
                  .map((fieldGroup: any) => (
                    <Fragment key={fieldGroup.name}>
                      <tr style={styles.performanceGroupName}>
                        <td colSpan={2}>{fieldGroup.name}</td>
                      </tr>
                      {fieldGroup?.items?.map((field: any) => (
                        <Fragment key={field.code}>
                          {commentData[field.code] && (
                            <tr style={styles.sitePerformanceEstimation}>
                              <td>{field.name}</td>
                              <td style={{ fontWeight: "bold" }}>
                                {`${numeral(commentData[field.code]).format(
                                  "0,0"
                                )} VND`}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </Fragment>
                  ))}
              </Table>
            </div>
          )}
          <div style={styles.imageContainer}>
            {commentData?.attachments?.map((attachment: any) => (
              <Fragment key={`${attachment.id}`}>
                {attachment.file_type === "image" ? (
                  <div
                    style={{
                      backgroundImage: `url(${attachment.file_url})`,
                      ...styles.imageAttachment,
                    }}
                    onClick={() => onImageClick(attachment.file_url, images)}
                  />
                ) : (
                  <a href={attachment.file_url}>
                    <div style={styles.fileAttachment}>
                      {attachment.file_name}
                    </div>
                  </a>
                )}
              </Fragment>
            ))}
          </div>
          <div style={styles.commentControlsContainer}>
            {commentData.permissions?.can_edit && isCanEdit && (
              <span
                style={styles.commentControlButton}
                onClick={() => setDisplayMode("edit_mode")}
              >
                Sửa
              </span>
            )}
          </div>
        </div>
      )}
      {displayMode === "edit_mode" && (
        <div style={styles.commentBoxDiv}>
          <SiteCommentBox
            siteId={commentData.site_id}
            comment={commentData}
            onCommentSaved={onCommentSaved}
            onCancelClicked={() => setDisplayMode("view_mode")}
            sitePerformanceEstimationFields={sitePerformanceEstimationFields}
          />
        </div>
      )}
      {imageBoxState?.showLightBox && (
        <Lightbox
          mainSrc={images[imageBoxState?.photoIndex]}
          nextSrc={images[(imageBoxState?.photoIndex + 1) % images.length]}
          prevSrc={
            images[
              (imageBoxState?.photoIndex + images.length - 1) % images.length
            ]
          }
          onCloseRequest={() =>
            setImageBoxState({ ...imageBoxState, ...{ showLightBox: false } })
          }
          onMovePrevRequest={() =>
            setImageBoxState({
              ...imageBoxState,
              ...{
                photoIndex:
                  (imageBoxState?.photoIndex + images.length - 1) %
                  images.length,
              },
            })
          }
          onMoveNextRequest={() =>
            setImageBoxState({
              ...imageBoxState,
              ...{
                photoIndex: (imageBoxState?.photoIndex + 1) % images.length,
              },
            })
          }
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "var(--color-line-light)",
    borderRadius: 6,
    padding: 16,
    marginTop: 12,
  },
  username: {
    fontSize: 14,
    fontWeight: 700,
  },
  createdDate: {
    fontSize: 12,
    color: "var(--color-muted)",
    marginLeft: 12,
  },
  editedLabel: {
    fontSize: 12,
    color: "var(--color-muted)",
    marginLeft: 4,
  },
  commentContentContainer: {
    marginTop: 6,
  },
  commentControlsContainer: {
    paddingTop: 6,
    paddingBottom: 6,
    display: "flex",
    justifyContent: "flex-end",
  } as React.CSSProperties,
  commentContentContent: {
    whiteSpace: "pre-line",
    wordWrap: "break-word",
  } as React.CSSProperties,
  commentControlButton: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--color-muted)",
    cursor: "pointer",
  },
  commentBoxDiv: {
    marginTop: 6,
  },
  sitePerformanceEstimation: {
    fontSize: 14,
  },
  imageContainer: {
    display: "flex",
    flexWrap: "wrap",
  } as React.CSSProperties,
  imageAttachment: {
    width: 90,
    height: 90,
    borderRadius: 6,
    marginRight: 12,
    marginBottom: 12,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    position: "relative",
    border: "1px solid var(--color-line)",
    cursor: "pointer",
  } as React.CSSProperties,
  fileAttachment: {
    marginRight: 12,
    height: 90,
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 3,
    padding: "6px 12px",
    backgroundColor: "var(--color-line)",
    cursor: "pointer",
  },
  performanceEstimationTable: {
    marginTop: 12,
  } as React.CSSProperties,
  performanceGroupName: {
    fontSize: 13,
    fontWeight: "bold",
    backgroundColor: "var(--color-line)",
  } as React.CSSProperties,
};

export default SiteComment;
